/**
 * CID Codes Filters Component
 * Inline filter bar: filters stay visible and apply live (no popover).
 *
 * @version 2025.1.0
 */
import React from 'react';
import { TextField } from '@mui/material';
import { InlineFilterBar, InlineSelect } from '../../../components/common';
import { CidCategory } from '../../../types/cidCode';
import { useFilterState } from '../../../hooks/useFilterState';

interface CidCodesFiltersData {
  search: string;
  code: string;
  category: string;
  isActive: string;
}

interface CidCodesFiltersProps {
  categories: CidCategory[];
  onFilter: (filters: CidCodesFiltersData) => void;
  loading?: boolean;
  total?: number;
}

const STATUS_OPTIONS = [
  { value: 'true', label: 'Ativos' },
  { value: 'false', label: 'Inativos' },
];

const defaultValues: CidCodesFiltersData = {
  search: '',
  code: '',
  category: '',
  isActive: '',
};

export const CidCodesFiltersComponent: React.FC<CidCodesFiltersProps> = ({
  categories,
  onFilter,
  loading = false,
  total,
}) => {
  const categoryOptions = categories.map((cat) => ({ value: cat.code, label: cat.code }));

  const { filters, setFilter, clearAll, activeChips } = useFilterState(defaultValues, {
    onFilter,
    applyOnMount: true,
    live: true,
    labels: {
      code: 'Código',
      category: 'Categoria',
      isActive: 'Status',
    },
    formatValue: {
      isActive: (v: string) => (v === 'true' ? 'Ativos' : 'Inativos'),
    },
  });

  return (
    <InlineFilterBar
      searchPlaceholder="Código, descrição..."
      searchValue={filters.search}
      onSearchChange={(v) => setFilter('search', v)}
      resultCount={total}
      resultNoun="códigos"
      chips={activeChips}
      onClearAll={clearAll}
      loading={loading}
    >
      <TextField
        label="Código exato"
        placeholder="Ex: M54.9"
        value={filters.code}
        onChange={(e) => setFilter('code', e.target.value.toUpperCase())}
        size="small"
        sx={{ minWidth: 130, maxWidth: 160 }}
        inputProps={{ style: { textTransform: 'uppercase' } }}
      />
      <InlineSelect
        label="Categoria"
        value={filters.category}
        options={categoryOptions}
        onChange={(v) => setFilter('category', v)}
        emptyLabel="Todas"
        minWidth={140}
      />
      <InlineSelect
        label="Status"
        value={filters.isActive}
        options={STATUS_OPTIONS}
        onChange={(v) => setFilter('isActive', v)}
        emptyLabel="Todos"
        minWidth={130}
      />
    </InlineFilterBar>
  );
};

export default CidCodesFiltersComponent;
